import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from "firebase/firestore";

interface Batch {
  id: string;
  name: string;
  class: string;
  location?: string;
  schedule?: string;
  startDate?: string;
  maxStudents?: number;
  curriculum?: string;
  createdAt?: any;
}
interface Chapter {
  id: string;
  class: string;
  chapterName: string;
}
interface Topic {
  id: string;
  class: string;
  chapterId: string;
  topicName: string;
}
interface Test {
  id: string;
  class: string;
  batchId: string;
  chapterIds: string[];
  topicIds: string[];
  testName: string;
  testDate: string;
  createdAt: any;
}

const classOptions = ["IX", "X", "XI", "XII"];

const MockAndTests: React.FC = () => {
  const [selectedClass, setSelectedClass] = useState("");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [testName, setTestName] = useState("");
  const [testDate, setTestDate] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);

  // For mapping IDs to names in the test table
  const [allBatches, setAllBatches] = useState<Batch[]>([]);
  const [allChapters, setAllChapters] = useState<Chapter[]>([]);
  const [allTopics, setAllTopics] = useState<Topic[]>([]);

  // Fetch batches, chapters, topics, tests
  useEffect(() => {
    if (selectedClass) {
      fetchBatches(selectedClass);
      fetchChapters(selectedClass);
      setSelectedBatchId("");
      setSelectedChapterId("");
      setSelectedTopicId("");
    } else {
      setBatches([]);
      setChapters([]);
      setTopics([]);
      setSelectedBatchId("");
      setSelectedChapterId("");
      setSelectedTopicId("");
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedChapterId) {
      fetchTopics(selectedClass, selectedChapterId);
    } else {
      setTopics([]);
      setSelectedTopicId("");
    }
  }, [selectedClass, selectedChapterId]);

  // Fetch all batches, chapters, topics for mapping
  useEffect(() => {
    const fetchAllBatches = async () => {
      const snapshot = await getDocs(collection(db, "batches"));
      const batchList = snapshot.docs
        .map(doc => ({ id: doc.id, ...(doc.data() as any) }))
        .filter((b): b is Batch => typeof b.name === 'string' && typeof b.class === 'string');
      setAllBatches(batchList);
    };
    const fetchAllChapters = async () => {
      const snapshot = await getDocs(collection(db, "chapters"));
      setAllChapters(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
    };
    const fetchAllTopics = async () => {
      const snapshot = await getDocs(collection(db, "topics"));
      setAllTopics(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
    };
    fetchAllBatches();
    fetchAllChapters();
    fetchAllTopics();
    fetchTests();
  }, []);

  const fetchBatches = async (cls: string) => {
    try {
      const q = query(collection(db, "batches"), where("class", "==", cls));
      const snapshot = await getDocs(q);
      // Filter out batches missing 'name' or 'class' to satisfy type
      const batchList = snapshot.docs
        .map(doc => ({ id: doc.id, ...(doc.data() as any) }))
        .filter((b): b is Batch => typeof b.name === 'string' && typeof b.class === 'string');
      setBatches(batchList);
    } catch {
      setBatches([]);
    }
  };
  const fetchChapters = async (cls: string) => {
    try {
      const q = query(collection(db, "chapters"), where("class", "==", cls));
      const snapshot = await getDocs(q);
      setChapters(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
    } catch {
      setChapters([]);
    }
  };
  const fetchTopics = async (cls: string, chapterId: string) => {
    try {
      const q = query(collection(db, "topics"), where("class", "==", cls), where("chapterId", "==", chapterId));
      const snapshot = await getDocs(q);
      setTopics(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
    } catch {
      setTopics([]);
    }
  };
  const fetchTests = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "tests"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      setTests(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
    } catch {
      setTests([]);
    }
    setLoading(false);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!selectedClass || !selectedBatchId || !selectedChapterId || !selectedTopicId || !testName || !testDate) {
      setError("Please fill all fields.");
      return;
    }
    try {
      await addDoc(collection(db, "tests"), {
        class: selectedClass,
        batchId: selectedBatchId,
        chapterIds: [selectedChapterId],
        topicIds: [selectedTopicId],
        testName,
        testDate,
        createdAt: Timestamp.now(),
      });
      setSuccess("Test added successfully!");
      setSelectedClass("");
      setSelectedBatchId("");
      setSelectedChapterId("");
      setSelectedTopicId("");
      setTestName("");
      setTestDate("");
      fetchTests();
    } catch {
      setError("Failed to add test.");
    }
  };

  // For mapping ids to names in the test table (use all batches/chapters/topics)
  const batchMap = Object.fromEntries(allBatches.map(b => [b.id, b.name]));
  const chapterMap = Object.fromEntries(allChapters.map(c => [c.id, c.chapterName]));
  const topicMap = Object.fromEntries(allTopics.map(t => [t.id, t.topicName]));

  return (
    <div className="w-full max-w-4xl mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-4">Mock and Tests</h2>
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block font-semibold mb-1">Class</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={selectedClass}
                onChange={e => setSelectedClass(e.target.value)}
                required
              >
                <option value="">Select Class</option>
                {classOptions.map(cls => (
                  <option key={cls} value={cls}>{`Class ${cls}`}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block font-semibold mb-1">Batch</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={selectedBatchId}
                onChange={e => setSelectedBatchId(e.target.value)}
                required
                disabled={!selectedClass || batches.length === 0}
              >
                <option value="">Select Batch</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block font-semibold mb-1">Chapter</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={selectedChapterId}
                onChange={e => {
                  setSelectedChapterId(e.target.value);
                  setSelectedTopicId("");
                }}
                required
                disabled={!selectedClass || chapters.length === 0}
              >
                <option value="">Select Chapter</option>
                {chapters.map(ch => (
                  <option key={ch.id} value={ch.id}>{ch.chapterName}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block font-semibold mb-1">Topic</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={selectedTopicId}
                onChange={e => setSelectedTopicId(e.target.value)}
                required
                disabled={!selectedChapterId || topics.length === 0}
              >
                <option value="">Select Topic</option>
                {topics.map(t => (
                  <option key={t.id} value={t.id}>{t.topicName}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="md:w-2/3 w-full">
              <label className="block font-semibold mb-1">Test Name</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={testName}
                onChange={e => setTestName(e.target.value)}
                required
              />
            </div>
            <div className="md:w-1/3 w-full">
              <label className="block font-semibold mb-1">Test Date</label>
              <input
                type="date"
                className="w-full border px-3 py-2 rounded"
                value={testDate}
                onChange={e => setTestDate(e.target.value)}
                required
              />
            </div>
          </div>
          {error && <div className="text-red-600">{error}</div>}
          {success && <div className="text-green-600">{success}</div>}
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full"
          >
            Add Test
          </button>
        </form>
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">All Tests</h3>
        {loading ? (
          <div>Loading...</div>
        ) : tests.length === 0 ? (
          <div>No tests found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-4 py-2">Class</th>
                  <th className="border px-4 py-2">Batch</th>
                  <th className="border px-4 py-2">Chapters</th>
                  <th className="border px-4 py-2">Topics</th>
                  <th className="border px-4 py-2">Test Name</th>
                  <th className="border px-4 py-2">Test Date</th>
                </tr>
              </thead>
              <tbody>
                {tests.map(test => (
                  <tr key={test.id}>
                    <td className="border px-4 py-2">Class {test.class}</td>
                    <td className="border px-4 py-2">{batchMap[test.batchId] || test.batchId}</td>
                    <td className="border px-4 py-2">{test.chapterIds.map((cid: string) => chapterMap[cid] || cid).join(", ")}</td>
                    <td className="border px-4 py-2">{test.topicIds.map((tid: string) => topicMap[tid] || tid).join(", ")}</td>
                    <td className="border px-4 py-2">{test.testName}</td>
                    <td className="border px-4 py-2">{test.testDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MockAndTests;
